import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';

// ── Admin → Client Downloads tab ──────────────────────────────────────────────
// Bulk export a client's scripts (one file per script, each in its own format
// template) and videos (live / AI / raw footage) for handoff. Scripts and videos
// are downloaded and reported separately — there is no combined output.

const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Backburner'];
const GB = 1024 ** 3;
const MB = 1024 ** 2;

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toISOString().slice(0, 10); } catch { return '—'; }
}
function fmtSize(bytes) {
  if (bytes == null || bytes === '') return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '—';
  return n >= GB ? `${(n / GB).toFixed(2)} GB` : `${(n / MB).toFixed(1)} MB`;
}
// Pull the server-supplied filename out of a Content-Disposition header.
function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const m = /filename="?([^"]+)"?/.exec(header);
  return m ? m[1] : fallback;
}
function saveBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function ClientDownloads() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  const client = clients.find(c => c.id === clientId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="data-section">
        <div className="data-section-title">Project</div>
        <div>
          <label className="vc-label">Select a project to export</label>
          <select
            className="filter-input"
            style={{ minWidth: 280, display: 'block' }}
            value={clientId}
            onChange={e => setClientId(e.target.value)}
          >
            <option value="">— Choose a project —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {clientId && (
        <>
          <ScriptsPanel key={`s-${clientId}`} clientId={clientId} client={client} />
          <VideosPanel  key={`v-${clientId}`} clientId={clientId} client={client} />
        </>
      )}
    </div>
  );
}

// ── Scripts panel ─────────────────────────────────────────────────────────────
function ScriptsPanel({ clientId, client }) {
  const [status, setStatus] = useState('');       // '' = All
  const [format, setFormat] = useState('csv');     // csv | doc
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [zipping, setZipping] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await api.get(`/api/client-downloads/${clientId}/scripts${q}`);
      setScripts(r.data.scripts || []);
      setSelected(new Set());
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to load scripts.');
      setScripts([]);
    } finally { setLoading(false); }
  }, [clientId, status]);

  useEffect(() => { load(); }, [load]);

  const allChecked = scripts.length > 0 && selected.size === scripts.length;
  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(scripts.map(s => s.id)));
  }
  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function downloadZip() {
    if (selected.size === 0) return;
    setZipping(true); setErr('');
    try {
      const res = await api.post(
        '/api/client-downloads/scripts/zip',
        { scriptIds: [...selected], format },
        { responseType: 'blob' },
      );
      const name = filenameFromDisposition(
        res.headers['content-disposition'],
        `${client?.initials || 'client'}-scripts.zip`,
      );
      saveBlob(res.data, name);
    } catch (e) {
      setErr('Could not build the scripts zip. Please try again.');
    } finally { setZipping(false); }
  }

  async function downloadReport() {
    setReporting(true); setErr('');
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await api.get(`/api/client-downloads/${clientId}/scripts/report${q}`, { responseType: 'blob' });
      const name = filenameFromDisposition(
        res.headers['content-disposition'],
        `${client?.initials || 'client'}-scripts-report.csv`,
      );
      saveBlob(res.data, name);
    } catch (e) {
      setErr('Could not build the scripts report.');
    } finally { setReporting(false); }
  }

  return (
    <div className="data-section">
      <div className="data-section-title">Scripts</div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="filter-input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {QUAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <span className="vc-label" style={{ margin: 0 }}>Format</span>
          {['csv', 'doc'].map(f => (
            <button
              key={f}
              className={`adm-tab ${format === f ? 'active' : ''}`}
              onClick={() => setFormat(f)}
              type="button"
            >{f.toUpperCase()}</button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <button className="btn-ghost" onClick={downloadReport} disabled={reporting}>
          {reporting ? 'Building…' : '↓ Scripts Report (CSV)'}
        </button>
        <button className="btn-primary" onClick={downloadZip} disabled={zipping || selected.size === 0}>
          {zipping ? 'Building…' : `↓ Download ${selected.size} script${selected.size === 1 ? '' : 's'} (zip)`}
        </button>
      </div>

      {err && <div style={{ color: 'var(--danger, #e5484d)', fontSize: 12 }}>{err}</div>}

      {loading ? (
        <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '16px 0' }}>Loading…</div>
      ) : scripts.length === 0 ? (
        <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '16px 0' }}>No scripts match this filter.</div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th" style={{ width: 36 }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                </th>
                <th className="adm-th">Script Name</th>
                <th className="adm-th">Format</th>
                <th className="adm-th">Writer</th>
                <th className="adm-th">Date Written</th>
                <th className="adm-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map(s => (
                <tr key={s.id}>
                  <td className="adm-td">
                    <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  </td>
                  <td className="adm-td primary">{s.script_name}</td>
                  <td className="adm-td">{s.format || '—'}</td>
                  <td className="adm-td">{s.writer_name}</td>
                  <td className="adm-td">{fmtDate(s.date_entered)}</td>
                  <td className="adm-td">{s.qual_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Videos panel ──────────────────────────────────────────────────────────────
const TYPE_LABELS = { live: 'Live videos', ai: 'AI videos', footage: 'Raw footage' };
const TYPE_BADGE  = { live: 'Live', ai: 'AI', footage: 'Raw Footage' };

function VideosPanel({ clientId, client }) {
  const [types, setTypes] = useState({ live: true, ai: false, footage: false });
  const [groupSize, setGroupSize] = useState(10);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [groups, setGroups] = useState([]);      // [{ id, label, items, sizeBytes, status, downloadUrl }]
  const [building, setBuilding] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [err, setErr] = useState('');
  const cancelRef = useRef(false);

  const activeTypes = Object.keys(types).filter(t => types[t]);

  const load = useCallback(async () => {
    if (activeTypes.length === 0) { setVideos([]); return; }
    setLoading(true); setErr('');
    try {
      const r = await api.get(`/api/client-downloads/${clientId}/videos?types=${activeTypes.join(',')}`);
      setVideos(r.data.videos || []);
      setSelected(new Set());
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to load videos.');
      setVideos([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, JSON.stringify(types)]);

  useEffect(() => { load(); }, [load]);

  const allChecked = videos.length > 0 && selected.size === videos.length;
  function toggleAll() { setSelected(allChecked ? new Set() : new Set(videos.map(v => v.id))); }
  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleType(t) { setTypes(prev => ({ ...prev, [t]: !prev[t] })); }

  // Selection, in list order.
  const selectedVideos = videos.filter(v => selected.has(v.id));
  const totalBytes = selectedVideos.reduce((sum, v) => sum + (Number(v.file_size_bytes) || 0), 0);
  const g = Math.max(1, Math.min(50, Number(groupSize) || 1));
  const numGroups = Math.ceil(selectedVideos.length / g);

  // Chunk selection into groups of g (list order).
  function chunkGroups() {
    const chunks = [];
    for (let i = 0; i < selectedVideos.length; i += g) {
      const items = selectedVideos.slice(i, i + g);
      chunks.push({
        id: `grp-${i / g + 1}`,
        label: `Group ${i / g + 1} of ${Math.ceil(selectedVideos.length / g)}`,
        items,
        sizeBytes: items.reduce((s, v) => s + (Number(v.file_size_bytes) || 0), 0),
        status: 'pending',
        downloadUrl: null,
      });
    }
    return chunks;
  }

  // Build one group: create the zip job, poll until complete/failed.
  async function buildOneGroup(group) {
    setGroups(prev => prev.map(x => x.id === group.id ? { ...x, status: 'building', downloadUrl: null } : x));
    try {
      const items = group.items.map(v => ({ type: v.type === 'footage' ? 'raw_footage' : 'video_asset', id: v.id }));
      const res = await api.post('/api/downloads/zip', { items });
      const { jobId } = res.data;
      // Poll (mirrors the existing card zip flow).
      const url = await new Promise((resolve, reject) => {
        const iv = setInterval(async () => {
          if (cancelRef.current) { clearInterval(iv); reject(new Error('cancelled')); return; }
          try {
            const poll = await api.get(`/api/downloads/zip/${jobId}`);
            if (poll.data.status === 'complete') { clearInterval(iv); resolve(poll.data.downloadUrl); }
            else if (poll.data.status === 'failed') { clearInterval(iv); reject(new Error('failed')); }
          } catch (e) { clearInterval(iv); reject(e); }
        }, 3000);
      });
      setGroups(prev => prev.map(x => x.id === group.id ? { ...x, status: 'complete', downloadUrl: url } : x));
      return true;
    } catch (e) {
      setGroups(prev => prev.map(x => x.id === group.id ? { ...x, status: 'failed', downloadUrl: null } : x));
      return false;
    }
  }

  // Build all groups sequentially, max 1 in flight.
  async function buildGroups() {
    if (selectedVideos.length === 0) return;
    cancelRef.current = false;
    const chunks = chunkGroups();
    setGroups(chunks);
    setBuilding(true); setErr('');
    for (const group of chunks) {
      if (cancelRef.current) break;
      await buildOneGroup(group);   // a failed group does not block the rest
    }
    setBuilding(false);
  }

  async function rebuildGroup(groupId) {
    const group = groups.find(x => x.id === groupId);
    if (!group) return;
    await buildOneGroup(group);
  }

  async function downloadReport() {
    if (activeTypes.length === 0) return;
    setReporting(true); setErr('');
    try {
      const res = await api.get(
        `/api/client-downloads/${clientId}/videos/report?types=${activeTypes.join(',')}`,
        { responseType: 'blob' },
      );
      const name = filenameFromDisposition(
        res.headers['content-disposition'],
        `${client?.initials || 'client'}-videos-report.csv`,
      );
      saveBlob(res.data, name);
    } catch (e) {
      setErr('Could not build the videos report.');
    } finally { setReporting(false); }
  }

  return (
    <div className="data-section">
      <div className="data-section-title">Videos</div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {Object.keys(TYPE_LABELS).map(t => (
          <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--tx-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={types[t]} onChange={() => toggleType(t)} />
            {TYPE_LABELS[t]}
          </label>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="vc-label" style={{ margin: 0 }}>Group size</span>
          <input
            className="filter-input"
            type="number" min={1} max={50}
            value={groupSize}
            onChange={e => setGroupSize(e.target.value)}
            style={{ width: 70 }}
          />
          <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>videos per zip file</span>
        </div>

        <div style={{ flex: 1 }} />

        <button className="btn-ghost" onClick={downloadReport} disabled={reporting || activeTypes.length === 0}>
          {reporting ? 'Building…' : '↓ Videos Report (CSV)'}
        </button>
      </div>

      {err && <div style={{ color: 'var(--danger, #e5484d)', fontSize: 12 }}>{err}</div>}

      {activeTypes.length === 0 ? (
        <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '16px 0' }}>Select at least one video type.</div>
      ) : loading ? (
        <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '16px 0' }}>Loading…</div>
      ) : videos.length === 0 ? (
        <div style={{ color: 'var(--tx-faint)', fontSize: 13, padding: '16px 0' }}>No videos of the selected types.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th" style={{ width: 36 }}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                  </th>
                  <th className="adm-th">Title</th>
                  <th className="adm-th">Type</th>
                  <th className="adm-th">Status</th>
                  <th className="adm-th">Date Uploaded</th>
                  <th className="adm-th">Size</th>
                </tr>
              </thead>
              <tbody>
                {videos.map(v => (
                  <tr key={`${v.type}-${v.id}`}>
                    <td className="adm-td">
                      <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggle(v.id)} />
                    </td>
                    <td className="adm-td primary">{v.title}</td>
                    <td className="adm-td">{TYPE_BADGE[v.type]}</td>
                    <td className="adm-td">{v.status || '—'}</td>
                    <td className="adm-td">{fmtDate(v.uploaded_at)}</td>
                    <td className="adm-td">{fmtSize(v.file_size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--tx-secondary)' }}>
              {selectedVideos.length === 0
                ? 'No videos selected.'
                : `${selectedVideos.length} video${selectedVideos.length === 1 ? '' : 's'} selected — will produce ${numGroups} zip file${numGroups === 1 ? '' : 's'} (group size ${g}), ~${(totalBytes / GB).toFixed(2)} GB total`}
            </div>
            <div style={{ flex: 1 }} />
            <button className="btn-primary" onClick={buildGroups} disabled={building || selectedVideos.length === 0}>
              {building ? 'Building…' : '⬇ Build zip groups'}
            </button>
          </div>

          {groups.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {groups.map(grp => (
                <div key={grp.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, color: 'var(--tx-primary)', fontWeight: 500 }}>
                    {grp.label} ({grp.items.length} video{grp.items.length === 1 ? '' : 's'}, {fmtSize(grp.sizeBytes)})
                  </div>
                  <div style={{ flex: 1 }} />
                  {grp.status === 'building' && <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Building…</span>}
                  {grp.status === 'pending' && <span style={{ fontSize: 12, color: 'var(--tx-faint)' }}>Queued</span>}
                  {grp.status === 'complete' && (
                    <>
                      <a className="btn-primary" href={grp.downloadUrl} rel="noopener" style={{ textDecoration: 'none' }}>↓ Download Zip</a>
                      <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>link expires in 1 hour</span>
                      <button className="btn-ghost" onClick={() => rebuildGroup(grp.id)}>↺ Rebuild</button>
                    </>
                  )}
                  {grp.status === 'failed' && (
                    <>
                      <span style={{ fontSize: 12, color: 'var(--danger, #e5484d)' }}>Failed</span>
                      <button className="btn-ghost" onClick={() => rebuildGroup(grp.id)}>↺ Rebuild</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
